import { useEffect, useMemo, useState } from 'react';
import type { AccountModel, NganHang, TaiKhoanNganHang, TaiKhoanNganHangInput } from '@/types/schemas';
import { AritoIcon, BottomActionBarV2, DialogV2, AritoFormV2 } from '@/components/custom/erp';
import { FormSchema, type FormValues, initialFormValues } from '../../schema';
import { QuickCreateScope } from './QuickCreateScope';
import { OtherInfoTab } from './OtherInfoTab';
import { BasicInfoTab } from './BasicInfoTab';
import { FormMode } from '@/types/form';

interface FormDialogProps {
  formMode: FormMode;
  open: boolean;
  initialData?: TaiKhoanNganHang;
  onSubmit?: (data: TaiKhoanNganHangInput) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  zIndex?: number;
}

const FormDialog = ({
  open,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy,
  formMode,
  initialData,
  zIndex
}: FormDialogProps) => {
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [nganHang, setNganHang] = useState<NganHang | null>(initialData?.ma_ngan_hang_data || null);
  const [taiKhoan, setTaiKhoan] = useState<AccountModel | null>(initialData?.account_model_data || null);

  useEffect(() => {
    if (!open) {
      setIsFormDirty(false);
    }
  }, [open]);

  useEffect(() => {
    setNganHang(initialData?.ma_ngan_hang_data || null);
    setTaiKhoan(initialData?.account_model_data || null);
  }, [initialData]);

  const data = useMemo(() => {
    if (!initialData) return initialFormValues;
    const legacyStatus = (initialData as TaiKhoanNganHang & { status?: string }).status;
    return {
      ...initialData,
      active: legacyStatus !== '0'
    };
  }, [initialData]);

  const handleSubmit = (formData: FormValues) => {
    const { active, ...rest } = formData;

    onSubmit?.({
      ...rest,
      ma_ngan_hang: nganHang?.uuid || '',
      ma_tai_khoan: rest.name,
      name: rest.name,
      account_model: taiKhoan?.uuid || null,
      active: !!active
    } as TaiKhoanNganHangInput);

    setIsFormDirty(false);
    onClose();
  };

  return (
    <>
      <DialogV2
        open={open}
        onClose={onClose}
        title={formMode === 'add' ? 'Mới' : formMode === 'edit' ? 'Sửa' : 'Danh mục tài khoản ngân hàng công ty'}
        maxWidth='lg'
        classNameContent='overflow-x-hidden'
        disableBackdropClose={false}
        disableEscapeKeyDown={true}
        titleIcon={<AritoIcon icon={281} />}
        zIndex={zIndex}
      >
        <QuickCreateScope>
          <AritoFormV2<FormValues>
            mode={formMode}
            hasAritoActionBar={false}
            schema={FormSchema}
            onSubmit={handleSubmit}
            initialData={data as Partial<FormValues>}
            title={formMode === 'add' ? 'Mới' : 'Sửa'}
            subTitle='Định mức nguyên vật liệu'
            className='w-full max-w-[900px] overflow-y-auto overflow-x-hidden'
            headerFields={
              <div onChange={() => setIsFormDirty(true)}>
                <BasicInfoTab
                  formMode={formMode}
                  setNganHang={setNganHang}
                  setTaiKhoan={setTaiKhoan}
                  nganHang={nganHang}
                  taiKhoan={taiKhoan}
                />
              </div>
            }
            tabs={[
              {
                id: 'other_info',
                label: 'Thông tin khác',
                component: (
                  <div onChange={() => setIsFormDirty(true)}>
                    <OtherInfoTab formMode={formMode} />
                  </div>
                )
              }
            ]}
            bottomBar={
              <BottomActionBarV2
                mode={formMode}
                onClose={onClose}
                onAdd={onAdd}
                onEdit={onEdit}
                onDelete={onDelete}
                onCopy={onCopy}
              />
            }
          />
        </QuickCreateScope>
      </DialogV2>
    </>
  );
};

export default FormDialog;
